import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, type JSX } from 'react';
import { SlaylistSelector, ToastContainer } from '@/components/features';
import DownloadApp from '@/components/features/DownloadApp/DownloadApp';
import { BottomNavbar, CollapsibleBottomSheet } from '@/components/layout';
import { useAuth } from '@/contexts/AuthContext';
import SignUpPage from '@/pages/SignUpPage';
import useAuthBottomSheetStore from '@/stores/authBottomSheetStore';
import useDownloadAppSheetStore from '@/stores/DownloadAppSheetStore';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import useLayoutStore from '@/stores/useLayoutStore';
import styles from './MainLayout.module.css';

const MainLayout = (): JSX.Element => {
  const [showNavbar, setShowNavbar] = useState(true);
  const isNavbarVisible = useLayoutStore((state) => state.isNavbarVisible);
  const location = useLocation();
  const { setSigned } = useAuth();
  const [signUpOpenedRef, setSignUpOpenedRef] = useState<boolean>(false);

  // Auth sheet state
  const { isSheetOpen, closeSheet, triggerSuccess } = useAuthBottomSheetStore();

  // Slaylist selector state
  const { isOpen, productId, closeSlaylistSelector, data } =
    useSlaylistSelectorStore();
  const isSlaylistOpenRef = useRef(isOpen);
  isSlaylistOpenRef.current = isOpen;

  // Download App sheet state
  const {
    isOpen: isDownloadSheetOpen,
    closeSheet: closeDownloadSheet,
    initialSlide,
  } = useDownloadAppSheetStore();
  const isDownloadSheetOpenRef = useRef(isDownloadSheetOpen);
  isDownloadSheetOpenRef.current = isDownloadSheetOpen;

  // Close modals on route changes
  useEffect(() => {
    if (isSlaylistOpenRef.current) {
      closeSlaylistSelector();
    }
    if (isDownloadSheetOpenRef.current) {
      closeDownloadSheet();
    }
  }, [location.pathname, closeSlaylistSelector, closeDownloadSheet]);

  useEffect(() => {
    if (isSheetOpen) {
      setSignUpOpenedRef(true);
    } else {
      setTimeout(() => {
        setSignUpOpenedRef(false);
      }, 200);
    }
  }, [isSheetOpen]);

  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = (): void => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const heightRatio = window.innerHeight / window.outerHeight;
        const isKeyboardActive = heightRatio < 0.6;
        const heightDifference = window.outerHeight - window.innerHeight;
        const isSignificantHeightChange = heightDifference > 200;

        if (isKeyboardActive && isSignificantHeightChange) {
          setShowNavbar(false);
        } else {
          setShowNavbar(true);
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  return (
    <div className={styles.layout}>
      <div className={styles.content}>
        <Outlet />
      </div>
      {showNavbar && isNavbarVisible && <BottomNavbar />}

      {/* Auth Bottom Sheet */}
      {(isSheetOpen || signUpOpenedRef) && (
        <CollapsibleBottomSheet
          isExpanded={isSheetOpen}
          maxHeight={window.innerHeight * 0.6}
          onCollapse={closeSheet}
          onExpandedChange={(expanded) => {
            if (!expanded) closeSheet();
          }}
        >
          <SignUpPage
            onAuthSuccess={(isSignUp, name) => {
              setSigned(true);
              triggerSuccess(isSignUp, name);
            }}
          />
        </CollapsibleBottomSheet>
      )}

      {/* Slaylist Selector */}
      {isOpen && (
        <>
          <div className={styles.overlay} onClick={closeSlaylistSelector} />
          <SlaylistSelector
            productId={productId}
            productImage={data?.productImage}
          />
        </>
      )}

      {/* Download App Bottom Sheet */}
      {isDownloadSheetOpen && (
        <>
          <div className={styles.overlay} onClick={closeDownloadSheet} />
          <CollapsibleBottomSheet
            isExpanded={isDownloadSheetOpen}
            maxHeight={window.innerHeight * 0.9}
            style={{ zIndex: 1002 }} // Ensures sheet is above the overlay
            onCollapse={closeDownloadSheet}
            onExpandedChange={(expanded) => {
              if (!expanded) {
                closeDownloadSheet();
              }
            }}
          >
            <DownloadApp initialSlide={initialSlide} />
          </CollapsibleBottomSheet>
        </>
      )}

      <ToastContainer />
    </div>
  );
};

export default MainLayout;
